/* eslint-disable camelcase */
import { isUsdStableCoin, round, round_by_tick_size } from 'helpers/formulas';
import { cropAfterDecimals } from 'helpers/utils';
import {
  heartbeatTSSelector as appHeartbeatTSSelector,
  publicHeartbeatTSSelector as appPublicHeartbeatTSSelector,
} from 'stores/app/app.selectors';
import { basketOrderOrderBookStoreSelector } from 'stores/basketOrderBook/basketOrderBook.selectors';
import { openPositionStoreSelector } from 'stores/openPosition/openPosition.selectors';
import { recentTradesStoreSelector } from 'stores/recentTrades/recentTrades.selectors';
import { selectedProductStateSelector } from 'stores/selectedProduct/selectedProduct.selectors';
import { walletStoreSelector } from 'stores/wallet/wallet.selectors';
import { Product } from 'types/IProducts';
import { Precision, ProductId } from 'types/ITrade';
import { wrapSelector } from 'variableStore/helpers';

import {
  selectedProductIdSelector,
  selectedProductPricePrecisionState,
  selectedProductState,
} from './tradeSelectors';

export const heartbeatTSSelector = appHeartbeatTSSelector;
export const publicHeartbeatTSSelector = appPublicHeartbeatTSSelector;
export const basketOrderOrderbooksSelector = () => basketOrderOrderBookStoreSelector();

// #region Zustand Non Reactive Selectors. Use selectors form stores folder instead for reactivity
export const walletSpotPriceSelector = () => walletStoreSelector().walletSpotPrice;
export const openPositionMarkDataSelector = () =>
  openPositionStoreSelector().openPositionMarkData;
export const selectedProductMarkDataSelector = () =>
  selectedProductStateSelector().markPrice;
export const selectedProductSpot = () => selectedProductStateSelector().spotPrice;
export const selectedProductFundingData = () =>
  selectedProductStateSelector().fundingData;
export const openPositionBestAsk = () => openPositionStoreSelector().openPositionBestAsk;
export const openPositionBestBid = () => openPositionStoreSelector().openPositionBestBid;
export const recentTradesSelector = () => recentTradesStoreSelector().trades;
// #endregion Zustand Non Reactive Selectors

/**
 * Note: Non-Reactive, use from useSelectedProductStoreBase / selectedProduct.selectors.ts
 */
const markPriceState = wrapSelector(
  [selectedProductIdSelector, selectedProductPricePrecisionState],
  (id: ProductId, precision: Precision) => {
    const markPriceObj = selectedProductMarkDataSelector();

    if (Number(id) === Number(markPriceObj.product_id)) {
      return round(markPriceObj.price!, precision);
    }
    return '-';
  }
);

/**
 * Note: Non-Reactive, use from useSelectedProductStoreBase / selectedProduct.selectors.ts
 */
const spotPriceState = wrapSelector(
  [selectedProductState],
  (selectedProduct: Product) => {
    const spotPrice = selectedProductStateSelector().spotPrice ?? 0;
    if (selectedProduct) {
      return round_by_tick_size(spotPrice, selectedProduct.tick_size!);
    }
    return '-';
  }
) as () => string;

const getNotionalInUsdt = ({
  size,
  contract_value,
  contract_unit_currency,
  spotPriceValue,
}: {
  size: number;
  contract_value: number;
  contract_unit_currency: string;
  spotPriceValue: number;
}) => {
  const settlingSpotPrice = isUsdStableCoin(contract_unit_currency) ? 1 : spotPriceValue;

  const notionalInUsdt =
    Math.abs(Number(size)) * Number(contract_value) * Number(settlingSpotPrice || 1);
  return cropAfterDecimals(notionalInUsdt, 2);
};

export { markPriceState, spotPriceState, getNotionalInUsdt };
